import { API } from '@/core/Api';

// 텍스트를 이미지로 변환하는 AI 서비스
export class TextToImageAI {
  /**
   * Flux Pro Ultra를 사용하여 텍스트를 고품질 이미지로 변환
   * @param prompt 이미지 생성을 위한 텍스트 프롬프트
   * @param options 추가 옵션들
   * @returns 생성된 이미지 URL들
   */
  async generateWithFluxProUltra(
    prompt: string,
    options: {
      aspect_ratio?: string; // 화면 비율 (예: "16:9", "1:1")
      num_images?: number; // 생성할 이미지 개수 (1-4)
      negative_prompt?: string; // 제외할 요소들
      output_format?: 'jpeg' | 'png'; // 출력 형식
      signal?: AbortSignal;
    } = {},
  ) {
    const requestData = {
      prompt,
      aspect_ratio: options.aspect_ratio || '1:1',
      num_images: options.num_images || 1,
      negative_prompt: options.negative_prompt,
      enable_safety_checker: true,
      safety_tolerance: '2',
      output_format: options.output_format || 'jpeg',
    };

    const response = await API.post('/api/ai/text-to-image/flux-pro-ultra', requestData, {
      signal: options.signal,
    });

    if (response.success && response.body) {
      return response.body;
    } else {
      throw new Error(response.detail || '이미지 생성에 실패했습니다.');
    }
  }

  /**
   * Flux Kontext를 사용하여 고급 이미지 생성
   * @param prompt 이미지 생성을 위한 텍스트 프롬프트
   * @param options 추가 옵션들
   * @returns 생성된 이미지 정보
   */
  async generateWithFluxKontext(
    prompt: string,
    options: {
      aspect_ratio?: string;
      num_images?: number;
      guidance_scale?: number; // CFG 가이던스 스케일 (1-20)
      sync_mode?: boolean; // 동기 모드
      signal?: AbortSignal;
    } = {},
  ) {
    const requestData = {
      prompt,
      aspect_ratio: options.aspect_ratio || '1:1',
      num_images: options.num_images || 1,
      guidance_scale: options.guidance_scale || 7.5,
      sync_mode: options.sync_mode || false,
      output_format: 'png',
      safety_tolerance: '2',
    };

    const response = await API.post('/api/ai/text-to-image/flux-kontext', requestData, {
      signal: options.signal,
    });

    if (response.success && response.body) {
      return response.body;
    } else {
      throw new Error(response.detail || '이미지 생성에 실패했습니다.');
    }
  }

  /**
   * GPT Image 1 (DALL-E)을 사용하여 이미지 생성
   * @param prompt 이미지 생성을 위한 텍스트 프롬프트
   * @param openaiApiKey OpenAI API 키 (필수)
   * @param options 추가 옵션들
   * @returns 생성된 이미지 정보
   */
  async generateWithGPTImage1(
    prompt: string,
    options: {
      image_size?: string; // '1024x1024' | '1536x1024' | '1024x1536';
      num_images?: number;
      quality?: 'standard' | 'high';
      background?: 'transparent' | 'opaque' | 'auto';
      signal?: AbortSignal;
    } = {},
  ) {
    const requestData = {
      prompt,
      image_size: options.image_size || '1024x1024',
      num_images: options.num_images || 1,
      quality: options.quality || 'high',
      background: options.background || 'opaque',
    };

    const response = await API.post('/api/ai/text-to-image/gpt-image-1', requestData, {
      signal: options.signal,
    });

    if (response.success && response.body) {
      return response.body;
    } else {
      throw new Error(response.detail || '이미지 생성에 실패했습니다.');
    }
  }

  /**
   * Nano Banana를 사용하여 텍스트를 이미지로 변환
   * @param prompt 이미지 생성을 위한 텍스트 프롬프트
   * @param options 추가 옵션들
   * @returns 생성된 이미지 정보
   */
  async generateWithNanoBanana(
    prompt: string,
    options: {
      num_images?: number;
      sync_mode?: boolean; // 동기 모드
      output_format?: 'jpeg' | 'png'; // 출력 형식
      signal?: AbortSignal;
      aspect_ratio?: string;
    } = {},
  ) {
    const requestData = {
      prompt,
      num_images: options.num_images || 1,
      sync_mode: options.sync_mode || false,
      output_format: options.output_format || 'jpeg',
      aspect_ratio: options.aspect_ratio || '3:4',
    };

    const response = await API.post('/api/ai/text-to-image/nano-banana', requestData, {
      signal: options.signal,
    });

    if (response.success && response.body) {
      return response.body;
    } else {
      // 더 자세한 에러 정보 로깅
      console.error('Nano Banana API Response:', response);
      const errorMessage = response.detail || 'Nano Banana 이미지 생성에 실패했습니다.';
      throw new Error(errorMessage);
    }
  }
}
